import React, { useEffect } from 'react';

import config from 'config';
import { BsArrowRight } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import ReviewsHeader from '~/layout/components/ReviewsHeader';
import BuilderSizeGuideModal from '~/pdp/components/BuilderSizeGuideModal';
import useOutfitSavingsBanner from '~/pdp/utils/useOutfitSavingsBanner';
import SizeFabricGuideModal from '~/shared/components/SizeFabricGuideModal';
import { productDetailPath } from '~/shared/constants/product';
import getImageUrl from '~/shared/utils/getImageUrl';
import { isIneligibleFitGuideProduct } from '~/shared/utils/isIneligibleFitGuideProduct';
import logger from '~/shared/utils/logger';
import { shouldUseAlternateSizeGuide } from '~/shared/utils/shouldUseAlternateSizeGuide';
import { useBuilderSizeGuide } from '~/shared/utils/useBuilderSizeGuide';
import SizeFabricGuideFableticsModal from '~/staticPages/components/SizeFabricGuideFableticsModal';
import { useMembership } from '~/techstyle-shared/react-accounts';
import {
  mobile,
  ProductImage as BaseProductImage,
  ProductName as BaseProductName,
  useProductDetailContext,
  useProductDetailSelectionState,
  useProductContext,
  useReviewsDrawer,
  ProductPrice,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import {
  trackProductClicked,
  useReviewsMetaDataQuery,
} from '~/techstyle-shared/react-products';

import { useAssortmentTest } from '../../context/AssortmentTestContext';
import { useProductQueryAndPreload } from '../../utils/useProductQueryAndPreload';
import ProductDetailColorSelector from '../ProductDetailColorSelector';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';
import ProductDetailEmbroiderySelector from '../ProductDetailEmbroiderySelector.js';
import ProductDetailOtherSelectors from '../ProductDetailOtherSelectors';
import ProductDetailSizeSelector from '../ProductDetailSizeSelector';
import ProductDetailsSection from '../ProductDetailsSection';
import ScrubsSizeGuideModal from '../ScrubsSizeGuideModal';

const debug = logger.extend('ProductDetails');

const outfitImageBreakpoints = [{ minWidth: 0, pictureWidth: 100 }];

const OutfitWrapper = styled.div`
  box-shadow:
    inset 0 0 0 1px ${({ theme }) => theme.colors.outfitItemWrapper},
    0 1px 2px 0 rgba(0, 0, 0, 0.08);
  background: ${({ theme }) => theme.colors.palette.white};
  margin-bottom: 24px;
  ${mobile`
    margin-bottom: 8px;
  `}
`;

const OutfitWrapperContent = styled.div`
  padding: 0;
  cursor: pointer;
`;

const OutfitDetailsWrapper = styled.div`
  padding: 16px;
`;

const OutfitWrapperActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.outfitItemActions};
  padding: 16px;
  cursor: pointer;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OutfitItemImageWrapper = styled.a`
  align-items: ${({ showItemPricing }) =>
    showItemPricing ? 'stretch' : 'center'};
  display: flex;
  margin-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.outfitItemImage};
  padding: 16px;
  margin-bottom: 0;
`;

const OutfitProductImage = styled(BaseProductImage)`
  flex-basis: 64px;
  flex-shrink: 0;
  margin-right: 12px;
  max-height: 96px;
`;

const OutfitProductName = styled(BaseProductName)`
  color: ${({ theme }) => theme.colors.sublabelBlack};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: ${-0.45 / 18}em;
`;

const sizeGuideButtonStyle = css`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  margin-bottom: 8px;
`;

const OutfitProductNameAndPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const OutfitProductPriceWrapper = styled.div``;

const OutfitProductVipPrice = styled.span`
  color: ${({ theme }) => theme.colors.palette.black};
  display: block;
  margin-bottom: 3px;
  font-size: 14px;
`;

const OutfitProductRetailPrice = styled.span`
  color: ${({ theme }) => theme.colors.palette.black};
  display: block;
  font-size: 14px;
`;

const priceStyle = css`
  ${({ theme }) => theme.productDetailPrice.priceStyle};
`;

const scrubsId = config.get('public.productCategories.scrubs');

const OutfitItem = () => {
  const product = useProductContext();
  const { openReviewsDrawer } = useReviewsDrawer();
  const {
    bundleProducts,
    updateBundleProduct,
    product: bundleProduct,
    sizeGuides: contextSizeGuides,
  } = useProductDetailContext();
  const { isPlusSizeSelected, selectedPermalink, productIndex } =
    useProductDetailSelectionState();
  const selectedProduct = bundleProducts?.[productIndex];
  const { membershipLevelGroup } = useMembership();
  const { data: reviewsData } = useReviewsMetaDataQuery(
    {
      groupCode: product?.groupCode,
    },
    { ssr: false, skip: !product?.groupCode }
  );
  const reviewCount = reviewsData?.reviewsMetaData?.counts?.reviewCount || 0;

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const imageType = isPlusSizeSelected ? 'plus' : 'default';

  const {
    excludeFplCodes,
    excludeTagIds,
    registerDisabledBundleItem,
    unregisterDisabledBundleItem,
    registerDisabledProduct,
    unregisterDisabledProduct,
  } = useAssortmentTest();

  const additionalQueryVariables = {
    includeSwatchType: true,
    membershipLevelGroup,
    filterOrphanedOptions: true,
    relatedProductExclusionTagIds: excludeTagIds,
    relatedProductExclusionFplCodes: excludeFplCodes,
  };

  debug('bundleProduct', bundleProduct);

  if (
    bundleProduct.componentExclusions &&
    bundleProduct.componentExclusions.length
  ) {
    const excludeSkus = bundleProduct.componentExclusions.map(
      (exclusion) => exclusion.sku
    );
    additionalQueryVariables.excludeSkus = excludeSkus;
    debug('Excluding SKUs', excludeSkus);
  }

  const {
    product: loadedSelectedProduct,
    relatedProductsLoading,
    baseProductLoading,
  } = useProductQueryAndPreload({
    permalinkOrProductId: selectedPermalink,
    initialProduct: selectedProduct,
    additionalQueryVariables,
  });

  const categoryIdList = product.productCategoryIdList;
  const tagIdList = product.tagIdList;
  const isScrubs = categoryIdList?.includes(scrubsId);
  const showFitGuide = !isIneligibleFitGuideProduct(product) && !isScrubs;
  const isLoading = relatedProductsLoading || baseProductLoading;
  const useAlternateSizeGuide = shouldUseAlternateSizeGuide(tagIdList);
  const isMysteryBox = product?.isMysteryBundle;

  const isAssortmentTestProductDisabled =
    (Array.isArray(excludeTagIds) &&
      selectedProduct?.tagIdList?.some((id) => excludeTagIds.includes(id))) ||
    (Array.isArray(excludeFplCodes) &&
      selectedProduct?.activeFeaturedProductLocationCodeList?.some(
        ({ featuredProductLocationCode }) =>
          excludeFplCodes.includes(featuredProductLocationCode)
      ));

  // Extract masterProductId to avoid depending on entire selectedProduct object
  const masterProductId = selectedProduct?.masterProductId;

  /*
    Note: we intentionally omitted isLoading in the dependencies
    because we do not want to dispatch an additional updateBundleProduct action
  */
  useEffect(() => {
    if (loadedSelectedProduct && !isLoading) {
      updateBundleProduct({
        product: loadedSelectedProduct,
        productIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedSelectedProduct, productIndex, updateBundleProduct]);

  // Register/unregister this outfit item as disabled (by index and masterProductId)
  useEffect(() => {
    if (isAssortmentTestProductDisabled) {
      registerDisabledBundleItem(productIndex);
      // Also register by masterProductId for attribute selector cross-out
      if (masterProductId) {
        registerDisabledProduct(masterProductId);
      }
    } else {
      unregisterDisabledBundleItem(productIndex);
      // If not disabled, also unregister the product ID
      if (masterProductId) {
        unregisterDisabledProduct(masterProductId);
      }
    }
    return () => {
      unregisterDisabledBundleItem(productIndex);
    };
  }, [
    isAssortmentTestProductDisabled,
    productIndex,
    masterProductId,
    registerDisabledBundleItem,
    unregisterDisabledBundleItem,
    registerDisabledProduct,
    unregisterDisabledProduct,
  ]);

  const useFableticsPDPFitGuide = config.get(
    'public.brand.features.useFableticsPDPFitGuide'
  );

  const handleClick = async (href) => {
    const imageUrl = getImageUrl({ product, imageType });
    const hasPromo =
      Boolean(product.promos?.promoCode) &&
      typeof product.promos?.promoCode === 'string';
    const productLabelDisplay = hasPromo
      ? formatMessage({
          id: product.promos.promoCode,
          defaultMessage: product.promos.promoCode,
        })
      : null;

    const updatedHref = href.split(/[#?]/)[0];
    const productInfo = {
      product: {
        ...product,
        promos: {
          ...product.promos,
          promoLabel: product.promos?.promoLabel || productLabelDisplay,
        },
      },
      href: updatedHref,
      fplId: product.featuredProductLocationIdList,
      image_url: imageUrl,
    };

    dispatch(
      trackProductClicked({
        ...productInfo,
        productUrl: href,
      })
    );

    window.location.href = href;
  };

  const { categoryIds: selectedProductCategoryIds = [] } = selectedProduct;

  const { builderContent: builderSizeGuide } = useBuilderSizeGuide({
    sizeGuides: useAlternateSizeGuide ? null : contextSizeGuides,
    categoryIdList,
    defaultCategoryId: product?.defaultCategoryId,
    tagIdList: product?.tagIdList,
  });

  const { showEmbroiderySelector, bundleItemProductId } =
    useProductDetailEmbroideryContext();

  const { showSavingsBanner } = useOutfitSavingsBanner(bundleProduct);

  return (
    <>
      <OutfitWrapper data-testid="pdp-outfit-item">
        <OutfitWrapperContent>
          <OutfitItemImageWrapper
            onClick={() =>
              handleClick(`/${productDetailPath}/${selectedPermalink}`)
            }
            showItemPricing={showSavingsBanner}
          >
            <OutfitProductImage
              breakpoints={outfitImageBreakpoints}
              disableHover
              type={imageType}
            />
            {showSavingsBanner ? (
              <OutfitProductNameAndPrice>
                <OutfitProductName />
                <OutfitProductPriceWrapper>
                  <OutfitProductVipPrice tabIndex={0}>
                    <FormattedMessage
                      id="site_pdp.pdp_token_only_price"
                      defaultMessage="VIP:"
                    />{' '}
                    <ProductPrice
                      priceStyle={priceStyle}
                      showRetailPrice={false}
                      strikeRegularPrice={false}
                      showVipPrice
                      showPromoPrice={false}
                    />
                  </OutfitProductVipPrice>
                  <OutfitProductRetailPrice>
                    <FormattedMessage
                      id="site_product_page.retail"
                      defaultMessage="Non-Member"
                    />
                    :&nbsp;
                    <ProductPrice
                      priceStyle={priceStyle}
                      showRetailPrice
                      strikeRegularPrice
                      showVipPrice={false}
                      showPromoPrice={false}
                    />
                  </OutfitProductRetailPrice>
                </OutfitProductPriceWrapper>
              </OutfitProductNameAndPrice>
            ) : (
              <OutfitProductName />
            )}
          </OutfitItemImageWrapper>
          <OutfitDetailsWrapper>
            <ProductDetailColorSelector
              relatedProductsLoading={isLoading}
              displayLoadingState
              disabled={isLoading}
              disableAllOptions={isAssortmentTestProductDisabled}
            />
            <ProductDetailSizeSelector
              disableAllOptions={isAssortmentTestProductDisabled}
            />
            {builderSizeGuide ? (
              <BuilderSizeGuideModal
                builderContent={builderSizeGuide}
                sizeGuideButtonStyle={sizeGuideButtonStyle}
              />
            ) : (
              <>
                {isScrubs && (
                  <ScrubsSizeGuideModal
                    categoryIdList={selectedProductCategoryIds}
                    showMessaging
                    showSizeCalloutMessage
                    sizeGuideButtonStyle={sizeGuideButtonStyle}
                    useAlternate={useAlternateSizeGuide}
                  />
                )}
                {showFitGuide && !useFableticsPDPFitGuide && (
                  <SizeFabricGuideModal useAlternate={useAlternateSizeGuide} />
                )}
                {showFitGuide && useFableticsPDPFitGuide && (
                  <SizeFabricGuideFableticsModal
                    categoryIdList={categoryIdList}
                    useAlternate={useAlternateSizeGuide}
                  />
                )}
              </>
            )}
            {showEmbroiderySelector &&
              product.masterProductId === bundleItemProductId && (
                <ProductDetailEmbroiderySelector />
              )}
            <ProductDetailOtherSelectors
              disableAllOptions={isAssortmentTestProductDisabled}
            />
          </OutfitDetailsWrapper>
        </OutfitWrapperContent>
        <ProductDetailsSection product={selectedProduct} showBundleStyles />
        {reviewCount > 0 && !isMysteryBox && (
          <OutfitWrapperActions onClick={() => openReviewsDrawer(product)}>
            <ReviewsHeader />
            <BsArrowRight />
          </OutfitWrapperActions>
        )}
      </OutfitWrapper>
    </>
  );
};

export default OutfitItem;
